import { useAuth } from '@on3/ui-lib/src/contexts/AuthProvider';
import { databaseUrl } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import Filter, { makeList } from 'components/Db/Filters/Filter';
import FilterTeams from 'components/Db/Filters/FilterTeams';
import { AdSidebarLayout } from 'components/Layouts/AdSidebarLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { MasseyRatings } from 'components/UI/MasseyRatings/MasseyRatings';
import { type ISchedulePageProps } from 'pages/college/[college]/[sport]/[year]/schedule';
import { ScheduleSchema } from 'store/schemas/Schedule';
import { useSortedConferences } from 'utils/hooks/useSortedConferences';

import { CollegeSchedule } from './Schedule/CollegeSchedule';
import styles from './SchedulePage.module.scss';

export const SchedulePage = (props: ISchedulePageProps) => {
  const { hasRdbAccess } = useAuth();
  const { schedule, siteData, filters } = props;
  const { relatedModel, list, pagination } = schedule || {};
  const { currentSite } = siteData || {};
  const { year, sport, organization } = relatedModel || {};
  const { years, sports, teams } = filters || {};
  const { defaultYears } = siteData || {};
  const sortedConferences = useSortedConferences(teams);
  const isCurrentYear = year === defaultYears?.footballSeasonYear;
  const isFootball = relatedModel?.sportKey === 1;
  const currentYear = new Date().getFullYear();

  const currentSeason =
    (isFootball
      ? defaultYears?.footballSeasonYear
      : defaultYears?.nonFootballSeasonYear) || currentYear;

  const filteredYears = years?.filter(
    (yr) => yr <= currentSeason + 1 && yr >= 2023,
  );
  const teamName = organization?.name || '';
  const mascot = organization?.mascot || '';
  const sportSlug = slugify(sport?.name);
  const title = `${teamName} ${
    !isCurrentYear ? year : ''
  } ${sport?.name} Schedule`.trim();
  const description =
    `View the ${year} ${teamName} ${sport?.name} Schedule at on3.com. The ${mascot} ${sport?.name} schedule includes opponents, date, time, and results.`.trim();
  const noIndex = (pagination?.count ?? 0) === 0;

  const canonical =
    `https://www.on3.com/college/${currentSite?.slug}/${sportSlug}${
      !isCurrentYear ? '/' + year : ''
    }/schedule/`.trim();

  let schema;

  if (list) {
    schema = new ScheduleSchema({
      name: teamName,
      url: canonical,
      sport: sport?.name || '',
      event: list || [],
    }).toJSON();
  }

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      noIndex={noIndex}
      schema={schema}
      title={title}
    >
      <AdSidebarLayout>
        <div className={styles.block}>
          {hasRdbAccess() && organization && (
            <a
              href={`${databaseUrl}/organization/${organization?.slug}-${organization?.key}/schedule/?sport=${sportSlug}&year=${year}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              Edit Schedule
            </a>
          )}
          <header>
            <h1>{title}</h1>
            <div className={styles.subHeader}>
              <div className={styles.filtersWrapper}>
                {teams && (
                  <FilterTeams name="college" teams={sortedConferences} />
                )}
                {sports && (
                  <Filter
                    list={sports}
                    name="sport"
                    selected={`${sport?.name}`}
                  />
                )}
                {filteredYears && (
                  <Filter
                    list={makeList(filteredYears)}
                    name="year"
                    pathname="/college/[college]/[sport]/[year]/schedule"
                    selected={`${year}`}
                  />
                )}
              </div>
              <MasseyRatings type="prediction" />
            </div>
          </header>
          <CollegeSchedule full relatedModel={relatedModel} schedule={list} />
        </div>
      </AdSidebarLayout>
    </PageWrapper>
  );
};
